import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { sfqSiteApi } from "../../api/feasibilitySite";
import {
  SECTIONS,
  groupBySection,
  findMissingRequired,
  pruneHidden,
  CLEARED_ANSWER,
} from "../Feasibility/FeasibilityEditor/data";
import SiteConsentGate from "./SiteConsentGate";
import SiteNarrativeViewer from "./SiteNarrativeViewer";
import SiteQuestionForm from "./SiteQuestionForm";
import { SITE_LANGS, normalizeSiteLang, siteText } from "./siteText";

const Page = styled.div`
  min-height: 100vh;
  background: #f4f6f9;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
`;

const TopBar = styled.div`
  background: #fff;
  border-bottom: 1px solid #dee2e6;
  padding: 14px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #0d6efd;
`;

const Confidential = styled.div`
  font-size: 12px;
  color: #dc3545;
  font-weight: 600;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const LangTabs = styled.div`
  display: flex;
  gap: 4px;
`;

const LangTab = styled.button`
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#0d6efd" : "#dee2e6")};
  background: ${(p) => (p.$active ? "#e7f1ff" : "#fff")};
  color: ${(p) => (p.$active ? "#0d6efd" : "#6c757d")};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
`;

const Body = styled.div`
  max-width: 900px;
  margin: 32px auto;
  padding: 0 20px 60px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  padding: 24px 28px;
  margin-bottom: 24px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const StudyTitle = styled.h1`
  font-size: 20px;
  color: #212529;
  margin: 0 0 8px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  font-size: 13px;
  color: #6c757d;
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 0 0 10px;
`;

const Hr = styled.hr`
  border: none;
  border-top: 1px solid #dee2e6;
  margin: 20px 0;
`;

const PwInput = styled.input`
  padding: 9px 14px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 15px;
  letter-spacing: 2px;
  width: 240px;
  &:focus { outline: none; border-color: #0d6efd; }
`;

const PwErrorMsg = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: #dc3545;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: 8px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  background: ${(p) => (p.$primary ? "#0d6efd" : p.$success ? "#198754" : "#6c757d")};
  color: #fff;
  font-weight: 500;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:hover:not(:disabled) { filter: brightness(0.9); }
`;

// Sits under the top bar rather than filling the viewport on its own: the language toggle
// lives up there, and a dead link has to be readable in the reader's language too.
const ErrorPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 70vh;
  gap: 16px;
  color: #dc3545;
  font-size: 16px;
  text-align: center;
  padding: 40px 20px;
`;

const SuccessPage = styled(Card)`
  text-align: center;
  padding: 48px;
`;

const SaveStatus = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

// Carries both reasons the form refuses to submit — unanswered required questions and a
// save that has not landed. They read as one thing to the respondent ("this is not going
// anywhere yet"), so they get one look.
const ErrorBanner = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  border: 1px solid #f5c2c7;
  border-radius: 4px;
  background: #f8d7da;
  color: #842029;
  font-size: 13px;
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 440px;
  padding: 24px 28px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.2);
`;

const DialogTitle = styled.h2`
  font-size: 17px;
  color: #212529;
  margin: 0 0 12px;
`;

const DialogText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: #495057;
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
`;

const SAVE_DEBOUNCE_MS = 2500;

// Build keyed map from answers array: { questionKey: answerRow }
function buildMap(answers = []) {
  const map = {};
  for (const a of answers) {
    map[a.questionKey] = a;
  }
  return map;
}

export default function FeasibilitySite() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | locked | consent | error | expired | submitted | active
  const [sfq,       setSfq]       = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers,   setAnswers]   = useState({});  // { questionKey: { answerYN, answerNumeric, answerText, specifyText } }
  // A code, not a sentence: every message on this page has to be able to change language
  // under the reader's feet, including one already on screen.
  const [saveMsg,   setSaveMsg]   = useState("");  // "" | "saving" | "saved" | "submitFailed"
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false); // final-submit confirmation dialog

  // The whole screen's language. It starts on the language this site's invitation mail was
  // written in — the server sends that with every payload — and the header toggle moves it.
  // Once the reader has chosen, no later payload overrides them: they are looking at the
  // page they asked for, and having the questionnaire flip back on load is worse than
  // having opened in the other language in the first place.
  const [lang, setLang] = useState("ko");
  // A ref, not state: nothing renders off it, and the payload handlers have to read it as
  // it is now — not as it was when their callback was created.
  const langChosenRef = useRef(false);

  const [missingKeys, setMissingKeys] = useState(() => new Set()); // unanswered required, after a submit attempt
  // A save that did not land. The answers are still queued for retry, but until one gets
  // through the screen is ahead of the server, and submitting from here would close the
  // questionnaire around whatever the server does hold.
  const [saveFailed,  setSaveFailed]  = useState(false);

  // { studyTitle, studyCode, expiredAt } off the 410 — which study this dead link was for.
  // A reader who holds invitations to several studies cannot otherwise tell which mail
  // they just opened, and that is exactly what they have to tell the CRA.
  const [expired,   setExpired]   = useState(null);
  const [consent,   setConsent]   = useState(null); // consent text + state, while the gate is up
  const [password,  setPassword]  = useState("");   // kept in memory after a successful verify
  const [pwInput,   setPwInput]   = useState("");
  const [pwError,   setPwError]   = useState("");  // "" | "wrongPassword" | "verifyFailed"
  const [verifying, setVerifying] = useState(false);

  const t = siteText(lang);

  const saveTimer = useRef(null);
  const pendingRef = useRef({});
  // The save currently on the wire, if any — see flush.
  const inFlightRef = useRef(null);
  // Mirrors `answers` so handleChange can read the latest map synchronously — the
  // checklist toggle fires two set() calls in one tick and the second must see the first.
  const answersRef = useRef({});

  const sections = useMemo(
    () => (questions.length ? groupBySection(questions) : SECTIONS),
    [questions]
  );

  // The language the invitation mail was written in, as it arrives on each payload. Applied
  // only until the reader touches the toggle; after that their choice is the page.
  const seedLang = useCallback((value) => {
    if (!value || langChosenRef.current) return;
    setLang(normalizeSiteLang(value));
  }, []);

  function chooseLang(next) {
    langChosenRef.current = true;
    setLang(next);
  }

  // Takes whatever /verify or /consent returned. Until 개인정보 동의 is given the server
  // withholds the questionnaire and sends the consent text instead, so there is nothing
  // here to fall through to — the gate is the state.
  const applyFullData = useCallback((data) => {
    const { inv, answers: a, questions: q } = data;
    seedLang(data.defaultLang || data.consent?.defaultLang);
    if (data.needsConsent) {
      setSfq(inv);
      setConsent(data.consent);
      setState("consent");
      return;
    }
    setConsent(null);
    setSfq(inv);
    const map = buildMap(a);
    answersRef.current = map;
    setAnswers(map);
    if (q && q.length) setQuestions(q);
    setState(["Submitted", "Reviewed", "Finalized"].includes(inv.status) ? "submitted" : "active");
  }, [seedLang]);

  // A 410 can arrive at any point, not just on the way in: the CRA can close this link
  // while the questionnaire is open — a mis-sent invitation being recalled. Every request
  // this page makes therefore has to be able to end on the expired screen, or a reader
  // whose link was just closed sits in front of a generic "저장 실패" retrying forever.
  // Returns whether it handled the error, so callers can fall through to their own.
  const handleGone = useCallback((err) => {
    if (err?.response?.status !== 410) return false;
    const body = err.response.data || {};
    seedLang(body.lang);
    setExpired(body);
    setState("expired");
    return true;
  }, [seedLang]);

  const doVerify = useCallback(async (pw) => {
    setVerifying(true);
    setPwError("");
    try {
      const res = await sfqSiteApi.verify(token, pw);
      setPassword(pw);
      applyFullData(res.data.data);
    } catch (err) {
      if (handleGone(err)) return;
      setPwError(err?.response?.status === 401 ? "wrongPassword" : "verifyFailed");
    } finally {
      setVerifying(false);
    }
  }, [token, applyFullData, handleGone]);

  // A token only resolves once its invitation has been sent, and sending is what mints
  // the password — so anything we can load here is password-protected. An unsent or
  // unknown token comes back 401 and lands on the invalid-link screen.
  //
  // Kept callable rather than inlined in the effect: "expired" and "invalid" are the two
  // screens a link can leave and come back from — a CRA reopens the link (Edit) or sends a
  // fresh one — and nothing on the page would otherwise ask again. See the retry below.
  const loadMeta = useCallback(async () => {
    try {
      const res = await sfqSiteApi.load(token);
      const data = res.data.data || {};
      // The locked screen is the first thing the mail's link shows, so it is already in
      // the mail's language — nothing waits for the password to be entered.
      seedLang(data.defaultLang);
      setSfq(data.inv); // metadata for the locked-screen header
      setState("locked");
    } catch (err) {
      if (!handleGone(err)) setState("error");
    }
  }, [token, seedLang, handleGone]);

  useEffect(() => { loadMeta(); }, [loadMeta]);

  // The expired screen is terminal: once it is up nothing re-checks, so a reader whose CRA
  // has just reopened or resent the link sits in front of a stale "expired" with no way
  // forward but a reload they have no reason to guess at. This is what that costs.
  const [rechecking, setRechecking] = useState(false);
  async function recheck() {
    setRechecking(true);
    try {
      await loadMeta();
    } finally {
      setRechecking(false);
    }
  }

  // Lets the browser (and a screen reader) know which language the page is actually in.
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Escape backs out of the confirmation, but not while the submit is already on its way.
  useEffect(() => {
    if (!confirming) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) setConfirming(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirming, submitting]);

  // The response is the same payload verify would have returned once consent is on file,
  // so the questionnaire opens without a second round trip. Errors propagate to the gate,
  // which shows them without dropping the reader back to the password screen.
  // No respondent address is collected anywhere on this page, so the consent record —
  // like every audit entry the response leaves — names the site, not a person.
  // The gate hands back the language that was on screen when the button was pressed; that
  // is what the server snapshots the consent under.
  const handleAgree = useCallback(async (consentLang) => {
    const res = await sfqSiteApi.consent(token, { lang: consentLang }, password);
    applyFullData(res.data.data);
  }, [token, password, applyFullData]);

  // Returns whether the server now holds everything typed so far. A failed save must say
  // so: the answers stay on screen either way, so nothing else would tell the respondent
  // that what they see is not what has been stored.
  const flush = useCallback(async () => {
    // Join a save already on its way instead of racing it. That request is holding deltas
    // that have left the queue, so a second caller would read the empty queue as "the
    // server has everything" — which is exactly the answer Submit must not get wrong.
    while (inFlightRef.current) await inFlightRef.current;

    const sending = pendingRef.current;
    const pending = Object.values(sending);
    if (!pending.length) return true;

    pendingRef.current = {};
    setSaveMsg("saving");
    const req = sfqSiteApi.saveAnswers(token, pending, password);
    inFlightRef.current = req.catch(() => {}); // joinable by the loop above, never rejects
    try {
      await req;
      setSaveMsg("saved");
      setSaveFailed(false);
      setTimeout(() => setSaveMsg(""), 2000);
      return true;
    } catch (err) {
      // Put the deltas back so the next flush retries them instead of losing them for
      // good. Anything typed while the request was in flight is newer and wins: each
      // entry is seeded from the whole answer, so the later one already subsumes this.
      pendingRef.current = { ...sending, ...pendingRef.current };
      setSaveMsg("");
      // A closed link is not a failure to retry — retrying can never succeed, and the
      // save banner would keep promising otherwise. The expired screen says what happened
      // and who to ask. The queue is left as it is; nothing more will be sent from here.
      if (handleGone(err)) return false;
      setSaveFailed(true);
      return false;
    } finally {
      inFlightRef.current = null;
    }
  }, [token, password, handleGone]);

  // Queue a delta for the next flush. Seeded from the current answer so a partially
  // touched question still saves its other fields.
  function queuePending(questionKey, fields) {
    const seed = pendingRef.current[questionKey] || { ...(answersRef.current[questionKey] || {}) };
    pendingRef.current[questionKey] = { ...seed, ...fields, questionKey };
  }

  function handleChange(questionKey, field, value) {
    const base = answersRef.current;
    const updated = {
      ...base,
      [questionKey]: { ...(base[questionKey] || { questionKey }), [field]: value },
    };
    const { answers: pruned, clearedKeys, specifyClearedKeys } = pruneHidden(sections, updated);

    answersRef.current = pruned;
    setAnswers(pruned);

    queuePending(questionKey, { [field]: value });
    for (const key of clearedKeys) queuePending(key, CLEARED_ANSWER);
    // Specify text whose trigger no longer matches — only that field, so the answer the
    // respondent just picked survives.
    for (const key of specifyClearedKeys) queuePending(key, { specifyText: null });

    // Drop the error as soon as a flagged question is answered (or disappears). Only
    // questions already flagged by a submit attempt are considered — answering one
    // question shouldn't light up everything else the site hasn't reached yet.
    if (missingKeys.size) {
      const stillMissing = new Set(findMissingRequired(sections, pruned).map((q) => q.key));
      const next = new Set([...missingKeys].filter((k) => stillMissing.has(k)));
      if (next.size !== missingKeys.size) setMissingKeys(next);
    }

    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
  }

  async function handleSave() {
    clearTimeout(saveTimer.current);
    return flush();
  }

  // The count is what is kept, not the sentence: the banner has to restate itself when the
  // language changes under it.
  function flagMissing(keys) {
    setMissingKeys(new Set(keys));
    document.getElementById(`q_${keys[0]}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  // Everything that can still send the respondent back to the form runs *before* the
  // confirmation opens — a dialog that asks "are you sure?" and then answers with a
  // validation error is worse than no dialog at all.
  async function handleSubmitClick() {
    // Nothing below this line can be trusted until the server has the answers: the
    // required-question gate reads local state, and submitting closes the questionnaire
    // around whatever was actually stored. The flush retries the queue, so pressing
    // Submit again is the way out of a transient failure.
    if (!(await handleSave())) return;

    // Only questions currently visible on screen can block submission — a conditional
    // question hidden by an unchecked trigger is never required.
    const missing = findMissingRequired(sections, answersRef.current);
    if (missing.length) {
      flagMissing(missing.map((q) => q.key));
      return;
    }

    setConfirming(true);
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await sfqSiteApi.submit(token, password);
      setMissingKeys(new Set());
      setState("submitted");
    } catch (err) {
      if (handleGone(err)) return;
      const serverMissing = err?.response?.data?.error?.missing;
      if (Array.isArray(serverMissing) && serverMissing.length) flagMissing(serverMissing);
      else setSaveMsg("submitFailed");
    } finally {
      setSubmitting(false);
      // Either way the dialog closes: on success the page is already the thank-you screen,
      // and on failure the message and the flagged questions are behind the backdrop.
      setConfirming(false);
    }
  }

  // The toggle rides in the header, so it is on every screen this page can show — a reader
  // stopped at the password box or at a dead link needs it as much as one filling the form.
  const header = (
    <TopBar>
      <Logo>{t.brand}</Logo>
      <TopBarRight>
        <LangTabs role="group" aria-label={t.langLabel}>
          {SITE_LANGS.map((l) => (
            <LangTab
              key={l.key}
              type="button"
              $active={lang === l.key}
              aria-pressed={lang === l.key}
              onClick={() => chooseLang(l.key)}
            >
              {l.label}
            </LangTab>
          ))}
        </LangTabs>
        <Confidential>{t.confidential}</Confidential>
      </TopBarRight>
    </TopBar>
  );

  // Study code, site and PI are the study's own data — only the labels turn over.
  const studyHeader = (
    <Card>
      <StudyTitle>{sfq?.studyTitle}</StudyTitle>
      <MetaRow>
        {sfq?.studyCode && <span>{t.studyCode}: <strong>{sfq.studyCode}</strong></span>}
        {sfq?.siteName  && <span>{t.site}: <strong>{sfq.siteName}</strong></span>}
        {sfq?.piName    && <span>{t.pi}: <strong>{sfq.piName}</strong></span>}
      </MetaRow>
    </Card>
  );

  if (state === "loading") {
    return (
      <Page>
        {header}
        <Body>
          <Card style={{ textAlign: "center", padding: 48, color: "#6c757d" }}>
            {t.loading}
          </Card>
        </Body>
      </Page>
    );
  }

  if (state === "locked") {
    return (
      <Page>
        {header}
        <Body>
          {studyHeader}

          <Card>
            <SectionLabel>{t.lockedHeading}</SectionLabel>
            <div style={{ fontSize: 14, color: "#495057", marginBottom: 16 }}>
              {t.lockedIntro}
            </div>
            <form
              onSubmit={(e) => { e.preventDefault(); if (pwInput.trim() && !verifying) doVerify(pwInput.trim()); }}
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}
            >
              <PwInput
                type="password"
                value={pwInput}
                onChange={(e) => { setPwInput(e.target.value); if (pwError) setPwError(""); }}
                placeholder={t.passwordPlaceholder}
                autoFocus
              />
              <Btn $primary type="submit" disabled={verifying || !pwInput.trim()}>
                {verifying ? t.verifying : t.verify}
              </Btn>
            </form>
            {pwError && <PwErrorMsg>{t[pwError]}</PwErrorMsg>}
          </Card>
        </Body>
      </Page>
    );
  }

  if (state === "consent") {
    return (
      <Page>
        {header}
        <Body>
          {studyHeader}

          <Card>
            <SiteConsentGate consent={consent} lang={lang} onAgree={handleAgree} />
          </Card>
        </Body>
      </Page>
    );
  }

  if (state === "expired") {
    return (
      <Page>
        {header}
        <ErrorPage>
          <div style={{ fontSize: 48 }}>⏱</div>
          <div><strong>{t.expiredTitle}</strong></div>
          {(expired?.studyTitle || expired?.studyCode) && (
            <div style={{ fontSize: 14, color: "#495057" }}>
              {expired.studyTitle}
              {expired.studyCode && ` (${expired.studyCode})`}
            </div>
          )}
          {expired?.expiredAt && (
            <div style={{ fontSize: 13, color: "#adb5bd" }}>
              {t.expiredOn(new Date(expired.expiredAt).toLocaleDateString(t.locale))}
            </div>
          )}
          <div style={{ fontSize: 14, color: "#6c757d" }}>{t.expiredContact}</div>
          {/* 담당자가 링크를 다시 열어준 뒤 이 화면에서 빠져나올 수 있는 유일한 길. */}
          <Btn onClick={recheck} disabled={rechecking}>
            {rechecking ? t.expiredRetryBusy : t.expiredRetry}
          </Btn>
        </ErrorPage>
      </Page>
    );
  }

  if (state === "error") {
    return (
      <Page>
        {header}
        <ErrorPage>
          <div style={{ fontSize: 48 }}>⚠️</div>
          <div><strong>{t.invalidTitle}</strong></div>
          <div style={{ fontSize: 14, color: "#6c757d" }}>{t.invalidContact}</div>
        </ErrorPage>
      </Page>
    );
  }

  return (
    <Page>
      {header}

      <Body>
        {/* Study info header */}
        {studyHeader}

        {state === "submitted" ? (
          <SuccessPage>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#198754" }}>
              {t.submittedTitle}
            </div>
            <div style={{ fontSize: 14, color: "#6c757d", marginTop: 8 }}>
              {t.submittedBody}
            </div>
          </SuccessPage>
        ) : (
          <>
            {/* Narrative — the study's own text, shown as authored. */}
            {sfq?.narrativeHtml && (
              <Card>
                <SectionLabel>{t.overview}</SectionLabel>
                <SiteNarrativeViewer html={sfq.narrativeHtml} />
              </Card>
            )}

            {/* Questions */}
            <Card>
              <SectionLabel>{t.questions}</SectionLabel>
              <SiteQuestionForm
                answers={answers}
                onChange={handleChange}
                disabled={false}
                questions={questions}
                missingKeys={missingKeys}
                lang={lang}
              />

              <Hr />

              <ActionRow>
                <Btn onClick={handleSave}>{t.save}</Btn>
                <Btn $success onClick={handleSubmitClick} disabled={submitting}>
                  {submitting ? t.submitting : t.submitFinal}
                </Btn>
                {saveMsg && <SaveStatus>{t[saveMsg]}</SaveStatus>}
              </ActionRow>

              {saveFailed && <ErrorBanner>{t.saveFailed}</ErrorBanner>}
              {/* Counted, not remembered as a sentence, so it restates itself if the
                  language is switched while it is up. */}
              {missingKeys.size > 0 && (
                <ErrorBanner>{t.missingRequired(missingKeys.size)}</ErrorBanner>
              )}
            </Card>

            <div style={{ fontSize: 12, color: "#adb5bd", textAlign: "center" }}>
              {t.footer}
            </div>

            {confirming && (
              <Backdrop
                // Only an idle dialog dismisses itself; once the request is in flight the
                // backdrop is just a shield, since cancelling here would not recall it.
                onClick={() => { if (!submitting) setConfirming(false); }}
              >
                <Dialog
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="submit-confirm-title"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DialogTitle id="submit-confirm-title">{t.confirmTitle}</DialogTitle>
                  <DialogText>{t.confirmBody}</DialogText>
                  <DialogActions>
                    <Btn type="button" onClick={() => setConfirming(false)} disabled={submitting}>
                      {t.cancel}
                    </Btn>
                    <Btn type="button" $success autoFocus onClick={handleSubmit} disabled={submitting}>
                      {submitting ? t.submitting : t.confirmSubmit}
                    </Btn>
                  </DialogActions>
                </Dialog>
              </Backdrop>
            )}
          </>
        )}
      </Body>
    </Page>
  );
}
